import { FetchSavedTexts } from 'components/Fetch'
import { PageCallout } from 'components/PageCallout'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useMatch, useNavigate } from 'react-router'
import { resetComposer, setComposerBulkUploadFileUrl, setComposerStatus } from 'services/compose'
import {
  activePostIdSelector,
  composerIsEmptySelector,
  multiPostsCountSelector
} from 'services/compose/selectors'
import { clearContentSelection } from 'services/content'
import { toggleCallout } from 'services/ui/actions'
import { calloutsSelector } from 'services/ui/selectors'
import { StoreThunkDispatch } from 'store/state'
import Actions from './Actions'
import styles from './Composer.pcss'
import ComposerFilesDropHandler from './ComposerFilesDropHandler'
import Content from './Content'
import Header from './Header'
import Network from './Network'
import ProfileSelection from './ProfileSelection'

export function Composer(props: { onClose: (reset?: boolean, redirectUrl?: string) => void }) {
  const navigate = useNavigate()
  const location = useLocation()
  const match = useMatch('/composer/:view')
  const dispatch = useDispatch<StoreThunkDispatch>()
  const composerCallout = useSelector(calloutsSelector).find(c => c.slug === 'composer')
  const multiPostsCount = useSelector(multiPostsCountSelector)
  const [mode, setMode] = useState<'default' | 'bulk'>(multiPostsCount > 0 ? 'bulk' : 'default')
  const [dropOverlayVisible, setDropOverlayVisible] = useState(false)
  const isEmpty = useSelector(composerIsEmptySelector)
  const isEditingPost = Boolean(useSelector(activePostIdSelector))
  const scrollingElement = useRef<HTMLDivElement>(null)

  // Scroll down/up one screen on Spacebar (Shift + Spacebar) press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }
      if (event.key === ' ' && scrollingElement.current) {
        const scrollAmount = event.shiftKey ? -window.innerHeight : window.innerHeight
        scrollingElement.current.scrollBy({
          top: scrollAmount,
          behavior: 'smooth'
        })
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (['single', 'multi'].includes(match?.params?.view || '')) {
      setMode(match?.params?.view === 'multi' ? 'bulk' : 'default')
      navigate('/composer', { replace: true, state: location.state })
    }
  }, [match?.params?.view, navigate, location.state])

  useEffect(() => {
    if (multiPostsCount > 0) {
      setMode('bulk')
    }
  }, [multiPostsCount])

  const cancelEvent = (e: Event) => {
    e.preventDefault()
  }

  const dismissCallout = () => {
    dispatch(toggleCallout('composer'))
  }

  useEffect(() => {
    window.addEventListener('dragover', cancelEvent, false)
    window.addEventListener('drop', cancelEvent, false)
    dispatch(clearContentSelection())

    return () => {
      window.removeEventListener('dragover', cancelEvent)
      window.removeEventListener('drop', cancelEvent)
    }
  }, [dispatch])

  const onUnload = useCallback((event: Event) => {
    event.preventDefault()
    event.returnValue = false
    return ''
  }, [])

  useEffect(() => {
    if (__DEV__) {
      return
    }
    if (!isEmpty) {
      window.addEventListener('beforeunload', onUnload)
    }
    return () => {
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [isEmpty, onUnload])

  const toggleMode = (value: 'default' | 'bulk') => {
    if (value === 'bulk') {
      setMode('bulk')
      dispatch(setComposerStatus({ network: 'generic', text: '', html: '' }))
    } else {
      setMode('default')
      dispatch(setComposerBulkUploadFileUrl({ url: undefined }))
      dispatch(resetComposer())
    }
  }

  const closeAndReset = (redirectUrl?: string) => {
    props.onClose(true, redirectUrl)
  }

  const showDropOverlay = useCallback((e: any) => {
    if (mode === 'default' && e.dataTransfer.types.includes('Files')) {
      setDropOverlayVisible(true)
    }
  }, [mode])

  const hideDropOverlay = useCallback(() => {
    setDropOverlayVisible(false)
  }, [])

  return (
    <Fragment>
      <ComposerFilesDropHandler visible={dropOverlayVisible} onClose={hideDropOverlay} />
      <div
        className={styles.composer}
        data-test="composer"
        ref={scrollingElement}
        data-testid="composer"
        onDragEnter={showDropOverlay}
        onDragEnd={hideDropOverlay}
      >
        {composerCallout && <PageCallout data={composerCallout} onClose={dismissCallout} />}
        <Header activeView={mode} onActiveViewChange={toggleMode} onClose={props.onClose} />
        {!isEditingPost && <ProfileSelection />}
        {!isEditingPost && <Content activeView={mode} />}
        <Network activeView={mode} />
        <Actions activeView={mode} onPostCreateStarted={closeAndReset} />
        <FetchSavedTexts />
      </div>
    </Fragment>
  )
}

export default Composer
